/**
 * Enough Markdown for a model's answer, and no more.
 *
 * Headings, paragraphs, bullet and numbered lists, fenced code, blockquotes,
 * pipe tables, and inline bold / italic / code / links. Nothing is ever set as HTML: the
 * text is split into React nodes, so a page's content that reached the model
 * and came back cannot smuggle markup onto this screen.
 *
 * `[n]` markers are the one addition. When the answer was given numbered
 * sources, a marker that names one becomes a control; one that does not is
 * left as plain text — it is a model writing a number, not a citation.
 */

import { Fragment, type ReactNode } from 'react'
import './markdown.css'

export interface MarkdownProps {
  text: string
  /** Source numbers the answer was actually given. Others stay plain text. */
  cited?: Set<number>
  onCite?: (index: number) => void
}

type Block =
  | { kind: 'p'; text: string }
  | { kind: 'h'; level: number; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[]; start: number }
  | { kind: 'code'; lang: string; text: string }
  | { kind: 'quote'; text: string }
  | { kind: 'table'; head: string[]; rows: string[][] }
  | { kind: 'hr' }

const FENCE = /^```(\w*)\s*$/
const HEADING = /^(#{1,4})\s+(.+?)\s*#*\s*$/
const BULLET = /^\s*[-*+]\s+(.*)$/
const NUMBERED = /^\s*(\d+)[.)]\s+(.*)$/
const QUOTE = /^>\s?(.*)$/
const RULE = /^(-{3,}|\*{3,}|_{3,})\s*$/
const TABLE_ROW = /^\s*\|.*\|\s*$/
const TABLE_SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/

/** The cells of one `| a | b |` line. */
function cells(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim())
}

export function parseBlocks(text: string): Block[] {
  const lines = text.replace(/\r\n/g, '\n').split('\n')
  const blocks: Block[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    if (!line.trim()) {
      i++
      continue
    }

    const fence = FENCE.exec(line)
    if (fence) {
      const body: string[] = []
      i++
      while (i < lines.length && !FENCE.test(lines[i])) body.push(lines[i++])
      i++ // closing fence, if any
      blocks.push({ kind: 'code', lang: fence[1], text: body.join('\n') })
      continue
    }

    const heading = HEADING.exec(line)
    if (heading) {
      blocks.push({ kind: 'h', level: heading[1].length, text: heading[2] })
      i++
      continue
    }

    if (RULE.test(line)) {
      blocks.push({ kind: 'hr' })
      i++
      continue
    }

    if (BULLET.test(line)) {
      const items: string[] = []
      while (i < lines.length && BULLET.test(lines[i])) {
        let item = BULLET.exec(lines[i++])![1]
        // A wrapped line indented under the bullet belongs to it.
        while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !BULLET.test(lines[i])) {
          item += ' ' + lines[i++].trim()
        }
        items.push(item)
      }
      blocks.push({ kind: 'ul', items })
      continue
    }

    const numbered = NUMBERED.exec(line)
    if (numbered) {
      const items: string[] = []
      const start = Number(numbered[1])
      while (i < lines.length && NUMBERED.test(lines[i])) {
        let item = NUMBERED.exec(lines[i++])![2]
        while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !NUMBERED.test(lines[i])) {
          item += ' ' + lines[i++].trim()
        }
        items.push(item)
      }
      blocks.push({ kind: 'ol', items, start })
      continue
    }

    if (QUOTE.test(line)) {
      const body: string[] = []
      while (i < lines.length && QUOTE.test(lines[i])) body.push(QUOTE.exec(lines[i++])![1])
      blocks.push({ kind: 'quote', text: body.join('\n') })
      continue
    }

    // A pipe table: a header row, a separator row, then rows until a blank.
    if (TABLE_ROW.test(line) && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1])) {
      const head = cells(line)
      i += 2
      const rows: string[][] = []
      while (i < lines.length && TABLE_ROW.test(lines[i])) rows.push(cells(lines[i++]))
      blocks.push({ kind: 'table', head, rows })
      continue
    }

    // A paragraph runs until a blank line or the start of any other block.
    const body: string[] = []
    while (
      i < lines.length &&
      lines[i].trim() &&
      !FENCE.test(lines[i]) &&
      !HEADING.test(lines[i]) &&
      !BULLET.test(lines[i]) &&
      !NUMBERED.test(lines[i]) &&
      !QUOTE.test(lines[i]) &&
      !RULE.test(lines[i]) &&
      !(TABLE_ROW.test(lines[i]) && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1]))
    ) {
      body.push(lines[i++])
    }
    blocks.push({ kind: 'p', text: body.join('\n') })
  }

  return blocks
}

/** Inline: `code`, **bold**, *italic*, [text](url), and [n] citations. */
const INLINE = /(`[^`\n]+`|\*\*[^*\n]+\*\*|\*[^*\n]+\*|\[[^\]\n]+\]\((?:https?:\/\/|mailto:)[^)\s]+\)|\[\d{1,2}\])/g

function renderInline(text: string, cited?: Set<number>, onCite?: (n: number) => void): ReactNode[] {
  const parts = text.split(INLINE)
  return parts.map((part, index) => {
    if (!part) return null
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={index}>{part.slice(1, -1)}</code>
    }
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{renderInline(part.slice(2, -2), cited, onCite)}</strong>
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={index}>{renderInline(part.slice(1, -1), cited, onCite)}</em>
    }
    const cite = /^\[(\d{1,2})\]$/.exec(part)
    if (cite) {
      const n = Number(cite[1])
      if (cited?.has(n)) {
        return (
          <button
            key={index}
            type="button"
            className="md-cite"
            title={`Source ${n}`}
            onClick={() => onCite?.(n)}
          >
            {n}
          </button>
        )
      }
      return <Fragment key={index}>{part}</Fragment>
    }
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part)
    if (link) {
      return (
        <a key={index} href={link[2]} target="_blank" rel="noreferrer noopener">
          {link[1]}
        </a>
      )
    }
    return <Fragment key={index}>{part}</Fragment>
  })
}

export function Markdown({ text, cited, onCite }: MarkdownProps) {
  const blocks = parseBlocks(text)
  return (
    <div className="md">
      {blocks.map((block, index) => {
        switch (block.kind) {
          case 'h': {
            const Tag = (`h${Math.min(block.level + 2, 6)}`) as 'h3' | 'h4' | 'h5' | 'h6'
            return <Tag key={index}>{renderInline(block.text, cited, onCite)}</Tag>
          }
          case 'ul':
            return (
              <ul key={index}>
                {block.items.map((item, j) => (
                  <li key={j}>{renderInline(item, cited, onCite)}</li>
                ))}
              </ul>
            )
          case 'ol':
            return (
              <ol key={index} start={block.start}>
                {block.items.map((item, j) => (
                  <li key={j}>{renderInline(item, cited, onCite)}</li>
                ))}
              </ol>
            )
          case 'code':
            return (
              <pre key={index} data-lang={block.lang || undefined}>
                <code>{block.text}</code>
              </pre>
            )
          case 'quote':
            return <blockquote key={index}>{renderInline(block.text, cited, onCite)}</blockquote>
          case 'table':
            return (
              <div key={index} className="md-table">
                <table>
                  <thead>
                    <tr>
                      {block.head.map((cell, j) => (
                        <th key={j}>{renderInline(cell, cited, onCite)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, r) => (
                      <tr key={r}>
                        {block.head.map((_, j) => (
                          <td key={j}>{renderInline(row[j] ?? '', cited, onCite)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          case 'hr':
            return <hr key={index} />
          default:
            return <p key={index}>{renderInline(block.text, cited, onCite)}</p>
        }
      })}
    </div>
  )
}
